import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, AlertTriangle, ShieldCheck } from "lucide-react";
import { promoteCheck, promoteHorse, STAGES, fmtD } from "@/lib/pipelineApi";
import { formatApiError } from "@/lib/auth";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { fmtMT } from "@/lib/mountainTime";

const stageLabel = (s) => (STAGES.find(([k]) => k === s) || [])[1] || "—";
const label = (v) => (v ? String(v).replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase()) : "—");
const errMsg = (e) => { const d = e.response?.data?.detail; return typeof d === "object" ? d.message : formatApiError(d) || "Something went wrong"; };

function Row({ k, v, id }) {
  return <div className="flex justify-between gap-3 py-1 border-b border-stone-100 last:border-0"><dt className="text-stone-500">{k}</dt><dd data-testid={`move-live-${id}`} className="font-medium text-stone-900 text-right">{v}</dd></div>;
}

function Summary({ s }) {
  const d = s.dates || {};
  const sale = [d.bidding_starts && `Bidding starts ${fmtD(d.bidding_starts)}`, d.close_date && `ends ${fmtD(d.close_date)}`].filter(Boolean).join(" · ") || "Not scheduled";
  return (
    <dl className="text-sm rounded-lg border border-stone-200 p-3">
      <Row id="name" k="Horse" v={s.name} />
      <Row id="seller" k="Seller" v={s.seller || "—"} />
      <Row id="stage" k="Pipeline stage" v={stageLabel(s.stage)} />
      <Row id="app-status" k="Application" v={label(s.application_status)} />
      <Row id="contract-status" k="Contract" v={label(s.contract_status)} />
      <Row id="ad-status" k="Advertisement" v={label(s.ad_status) === "—" ? "Not started" : label(s.ad_status)} />
      <Row id="media" k="Pictures / videos" v={`${s.photos} ad photos${s.application_photos ? ` (+${s.application_photos} application)` : ""} · ${s.videos} videos`} />
      <Row id="dates" k="Scheduled sale" v={sale} />
    </dl>
  );
}

function Notes({ s }) {
  return (
    <ul className="text-xs text-stone-600 list-disc pl-5 space-y-1">
      <li>The horse becomes a production (LIVE) record. All information, media, ad, schedule, messages and contracts stay linked; nothing is copied.</li>
      <li>Nothing is published, posted to Facebook, emailed, texted or charged, and bidding dates don't change.</li>
      {s.ad_will_reset && <li data-testid="move-live-ad-reset" className="text-amber-800 font-semibold">The TEST ad is {s.ad_status}; it will be set back to Approved and won't appear on the LIVE website until you publish it.</li>}
      {s.pending_notifications > 0 && <li data-testid="move-live-notif">{s.pending_notifications} pending TEST notification(s) will be cancelled.</li>}
      {s.unsigned_contracts > 0 && <li>{s.unsigned_contracts} unsigned contract draft(s) become LIVE drafts (no email is sent).</li>}
    </ul>
  );
}

const TestContracts = ({ s }) => (
  <ul className="text-xs list-disc pl-5">{s.signed_test_contracts.map((k) => (
    <li key={k.id}>Version {k.version} ({label(k.kind)}) signed by {k.signer_name || "—"}{k.signed_at ? ` on ${fmtMT(k.signed_at)}` : ""}</li>))}</ul>
);

export function MoveToLiveDialog({ c, onClose, onDone }) {
  const [s, setS] = useState(null);
  const [step, setStep] = useState(0);
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { promoteCheck(c.key).then(setS).catch((e) => { toast.error(errMsg(e)); onClose(); }); }, [c.key]); // eslint-disable-line react-hooks/exhaustive-deps
  const hasTestK = !!s?.signed_test_contracts?.length;
  const last = hasTestK ? 2 : 0;
  const go = async () => {
    if (step < last) { setStep(step + 1); setAck(false); return; }
    setBusy(true);
    try {
      await promoteHorse({ key: c.key, confirm: true, test_contract_warning_ack: hasTestK, accept_test_contract: hasTestK });
      toast.success(`${s.name} moved to LIVE`); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!s) return null;
  const blocked = !s.eligible;
  const okLabel = blocked ? "Close" : step < last ? "Continue" : hasTestK ? "Accept TEST contract and move to LIVE" : "Move to LIVE";
  return (
    <AlertDialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <AlertDialogContent data-testid="move-live-dialog" className="max-h-[90vh] overflow-y-auto">
        <AlertDialogHeader>
          <AlertDialogTitle data-testid="move-live-title" className="text-left">Move {s.name} to LIVE?</AlertDialogTitle>
          <AlertDialogDescription asChild><div className="text-sm text-stone-600 space-y-3 text-left">
            {step === 0 && <><Summary s={s} />{blocked
              ? <div data-testid="move-live-blockers" className="rounded-lg border border-red-200 bg-red-50 text-red-800 p-2 font-semibold">{s.blockers.map((b) => <p key={b}>{b}</p>)}</div>
              : <Notes s={s} />}</>}
            {step === 1 && <div data-testid="move-live-contract-warning" className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 p-3 space-y-2">
              <p className="font-bold flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" />Confirmation 1 of 2: signed TEST contract</p>
              <p>This horse has a signed TEST contract and is being moved into the LIVE (production) environment.</p><TestContracts s={s} /></div>}
            {step === 2 && <div data-testid="move-live-contract-accept" className="rounded-lg border border-stone-300 bg-stone-50 text-stone-900 p-3 space-y-2">
              <p className="font-bold flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" />Confirmation 2 of 2: accept the TEST contract</p>
              <p>The existing TEST contract will be accepted for this LIVE horse, and you are authorizing the transfer. The contract and its original signature details stay exactly as they are and remain labelled TEST. No new contract is sent.</p>
              <p className="text-xs">Your name and the date and time are recorded with the transfer.</p></div>}
            {!blocked && <label className="flex items-start gap-2 text-stone-800 font-medium">
              <Checkbox data-testid="move-live-ack" checked={ack} onCheckedChange={(v) => setAck(!!v)} className="mt-0.5" />
              {step === 0 ? "I understand this will make the horse a production record." : step === 1 ? "I understand this horse has a signed TEST contract." : "I accept the TEST contract for this LIVE horse and authorize the transfer."}
            </label>}
          </div></AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          {!blocked && <Button data-testid="move-live-cancel" variant="outline" disabled={busy} onClick={onClose}>Cancel</Button>}
          <Button data-testid="move-live-confirm" disabled={busy || (!blocked && !ack)} onClick={blocked ? onClose : go}
            className="bg-emerald-700 hover:bg-emerald-800 text-white">{busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}{okLabel}</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
